import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleClassIdToObject,
  selectScheduleConstraints,
  selectScheduleCurrentFilters,
  selectScheduleData,
  selectScheduleHasUnpublishedChanges,
  selectScheduleIsFreeEditMode,
  selectScheduleMutationBlocked,
  selectScheduleTeamFilterOptions,
  selectScheduleTournamentInfo,
} from '../../../../../features/schedule/scheduleSelectors';
import { deriveDisplayScheduleView } from '../scheduleDisplayView';
import { getTournamentTimezone } from '../schedule.helpers';
import { buildScheduleFilterSummary } from './scheduleExportFilters';
import {
  buildScheduleCsvFilename,
  buildScheduleCsvRows,
} from './scheduleExportCsv';
import {
  buildSchedulePrintModel,
  type SchedulePrintMapBuildOptions,
} from './schedulePrintModel';

export function useScheduleExport(selectedDayKey: number) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const [printModalOpen, setPrintModalOpen] = useState(false);

  const schedule = useAppSelector(selectScheduleData);
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);
  const currentFilters = useAppSelector(selectScheduleCurrentFilters);
  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);
  const teamFilterOptions = useAppSelector(selectScheduleTeamFilterOptions);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const hasUnpublishedChanges = useAppSelector(selectScheduleHasUnpublishedChanges);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const isFreeEditMode = useAppSelector(selectScheduleIsFreeEditMode);

  const scheduleTimeZone = getTournamentTimezone(
    tournamentInfo?.country,
    tournamentInfo?.timeZone
  );

  const displayView = useMemo(
    () => deriveDisplayScheduleView(schedule, currentFilters, selectedDayKey),
    [schedule, currentFilters, selectedDayKey]
  );

  const dayTitleForKey = useMemo(
    () => (dayKey: number) =>
      t(prefix + 'scheduleSettingsDayLabel', { day: dayKey + 1 }),
    [prefix, t]
  );

  const filterSummary = useMemo(
    () =>
      buildScheduleFilterSummary(
        currentFilters,
        classIdToObject,
        teamFilterOptions,
        t,
        prefix
      ),
    [classIdToObject, currentFilters, prefix, t, teamFilterOptions]
  );

  const statusLabel = useMemo((): 'published' | 'draft' | '' => {
    if (hasUnpublishedChanges) return 'draft';
    if (scheduleMutationBlocked && !isFreeEditMode) return 'published';
    return '';
  }, [hasUnpublishedChanges, isFreeEditMode, scheduleMutationBlocked]);

  const generatedAtLabel = useMemo(
    () => formatInTimeZone(new Date(), scheduleTimeZone, 'yyyy-MM-dd HH:mm'),
    [scheduleTimeZone]
  );

  const buildPrintModel = useCallback(
    (mapOptions?: SchedulePrintMapBuildOptions) => {
      if (!displayView) return null;
      return buildSchedulePrintModel(
        displayView,
        scheduleConstraints,
        scheduleTimeZone,
        classIdToObject,
        { filterSummary },
        tournamentInfo?.name ?? t(prefix + 'schedule'),
        generatedAtLabel,
        statusLabel,
        dayTitleForKey,
        mapOptions
      );
    },
    [
      classIdToObject,
      dayTitleForKey,
      displayView,
      filterSummary,
      generatedAtLabel,
      prefix,
      scheduleConstraints,
      scheduleTimeZone,
      statusLabel,
      t,
      tournamentInfo?.name,
    ]
  );

  const printCourtNames = useMemo(
    () => displayView?.days[0]?.courts.map((c) => c.court.name) ?? [],
    [displayView]
  );

  const printCategoryOptions = useMemo(
    () =>
      Object.values(classIdToObject)
        .map((category) => ({
          value: category.id,
          label: category.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [classIdToObject]
  );

  const csvRows = useMemo(
    () =>
      buildScheduleCsvRows(
        displayView,
        scheduleConstraints,
        scheduleTimeZone,
        classIdToObject,
        dayTitleForKey
      ),
    [classIdToObject, dayTitleForKey, displayView, scheduleConstraints, scheduleTimeZone]
  );

  const csvFilename = useMemo(
    () =>
      buildScheduleCsvFilename(
        tournamentInfo?.id,
        selectedDayKey,
        formatInTimeZone(new Date(), scheduleTimeZone, 'yyyy-MM-dd')
      ),
    [scheduleTimeZone, selectedDayKey, tournamentInfo?.id]
  );

  const printDocumentTitle = useMemo(() => {
    const name = tournamentInfo?.name ?? 'Schedule';
    const dayPart = t(prefix + 'scheduleSettingsDayLabel', { day: Number(selectedDayKey) + 1 });
    return `${name} - ${dayPart} Schedule`;
  }, [prefix, selectedDayKey, t, tournamentInfo?.name]);

  const exportDisabled = csvRows.length === 0;

  return {
    prefix,
    t,
    printModalOpen,
    setPrintModalOpen,
    buildPrintModel,
    printCourtNames,
    printCategoryOptions,
    csvRows,
    csvFilename,
    printDocumentTitle,
    exportDisabled,
  };
}
